import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FileUploader } from 'ng2-file-upload';
import { environment } from '../../../environments/environment';
import { ToastRef, ToastrService } from 'ngx-toastr';
import { DocumentUploaderService } from '../../shared/documents/document-uploader.service';
import { DocumentUploadOptions } from '../../core/constants/DocumentUploadOptions';
import { Area } from '../../core/constants/area';
import { HttpClient } from '@angular/common/http';
import { ConsoleLogger } from '../../core/utilities/console-logger';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-file-import',
  templateUrl: './file-import.component.html',
  styleUrls: ['./file-import.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FileImportComponent implements OnInit {

  public URL = `${environment.dotNetApiUrl}/document`;

  public fileDragged = false;
  public fileReady = false;
  public isParsing = false;
  public isTxtFile = false;

  public fileType: any = '';
  public summaryInfo: any;
  public fileTypes: any[];
  public previewColumns: any[];
  public previewRows: any[];
  public rawTextContent = '';

  public csvInfo: any
  public documentUploader: any;

  constructor(
    private http: HttpClient,
    private toastrService: ToastrService,
    private documentUploaderService: DocumentUploaderService,
  ) { }

  ngOnInit() {
    this.fileTypes = DocumentUploadOptions.portalCSV;
    this.GetFactoringSummary();
    this.setupFileUploader();
  }

  public setupFileUploader(){
    this.documentUploader = new FileUploader({
      url: this.URL,
      headers: [],
      disableMultipart: true,
      formatDataFunctionIsAsync: true,
      formatDataFunction: async (item) => {
        return new Promise((resolve, reject) => {
          resolve({
            name: item._file.name,
            length: item._file.size,
            contentType: item._file.type,
            date: new Date()
          });
        });
      },
      filters: [
        {
          name: 'fileFilter',
          fn: (item: any) => {
            const fileType = item.type;
            return fileType.includes('csv') || fileType.includes('spreadsheetml') || fileType.includes('text/plain'); 
          }
        }
      ]
    });
  }

  public onDragover() {
    this.fileDragged = true;
  }

  public onDragout() {
    this.fileDragged = false;
  }

  GetFactoringSummary() {
    this.isParsing = true;
    this.http.get<any>(`${environment.dotNetApiUrl}/${Area.factoring}/Report/List`,{})
    .subscribe(x => {
        this.summaryInfo = x[0];
        ConsoleLogger.logInfo(this.summaryInfo);
        this.isParsing = false;
      },
      (err)=>{
        console.error('Error:', err);
        this.isParsing = false;
    });
  }

  public uploadDocumentFile(){
    if (this.documentUploader.queue[0]) {
      const file = this.documentUploader.queue[0].file.rawFile;
      const reader = new FileReader();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      // Added support for txt format files
      if (fileExtension === 'csv') {
        reader.onload = e => this.processCSV(reader.result.toString());
        reader.readAsText(file);
      } else if (fileExtension === 'xlsx') {
        reader.onload = e => this.processXLSX(reader.result);
        reader.readAsBinaryString(file); // Read as binary for XLSX
      } else if (fileExtension === 'txt' && this.fileType && this.fileType.fileTypes && Array.isArray(this.fileType.fileTypes) && this.fileType.fileTypes.includes("txt")) {
        reader.onload = e => this.processTXT(reader.result.toString());
        reader.readAsText(file);
        this.isTxtFile = true;
      } else {
        // Format the file types list with proper grammar
        const fileTypes = this.fileType.fileTypes || [];
        let acceptedTypesMessage = '';
        
        if (fileTypes.length === 1) {
          acceptedTypesMessage = fileTypes[0];
        } else if (fileTypes.length === 2) {
          acceptedTypesMessage = `${fileTypes[0]} and ${fileTypes[1]}`;
        } else if (fileTypes.length > 2) {
          const lastType = fileTypes[fileTypes.length - 1];
          const otherTypes = fileTypes.slice(0, fileTypes.length - 1).join(', ');
          acceptedTypesMessage = `${otherTypes}, and ${lastType}`;
        } else {
          acceptedTypesMessage = 'CSV, XLSX, and TXT';
        }
        this.documentUploader.clearQueue();
        this.toastrService.warning(`Only ${acceptedTypesMessage} files are accepted.`);
        return;
      }

      this.fileReady = true;
      this.fileDragged = false;
    } else {
        this.toastrService.warning("No file selected.");
    }
  }

  private processCSV(csvContent) {
    const rows = [];
    let inQuotes = false;
    let currentRow = [];
    let currentField = '';

    for (let i = 0; i < csvContent.length; i++) {
        const char = csvContent[i];
        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            if (currentField.startsWith('=')) {
                currentField = currentField.substring(1);
            }
            currentRow.push(currentField);
            currentField = '';
        } else if (char === '\n' && !inQuotes) {
            if (currentField.startsWith('=')) {
                currentField = currentField.substring(1);
            }
            currentRow.push(currentField);
            rows.push(currentRow);
            currentRow = [];
            currentField = '';
        } else {
            currentField += char;
        }
    }

    if (currentField !== '') { currentRow.push(currentField); }
    if (currentRow.length > 0) { rows.push(currentRow); }

    this.previewCSV(rows);
  }

  private processXLSX(binaryData) {
    const workbook = XLSX.read(binaryData, { type: 'binary' });

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    this.previewCSV(jsonData);
  }

  private processTXT(textContent) {
    this.rawTextContent = textContent;
  }

  public finishUpload(){
    const item = this.documentUploader.queue[0];
    this.documentUploaderService.importFile(item, this.fileType.key)
    .toPromise()
    .then((res) => {
        this.documentUploader.clearQueue();
        ConsoleLogger.logInfo({'File Upload Type': res});
        this.resetUpload()
        this.GetFactoringSummary();
      }, (err) => {
        console.error(err);
      });
  }

  public previewCSV(csv){
    const header = csv.shift();
    this.previewColumns = [];
    for(let i = 0; i < header.length; i++){
        this.previewColumns.push({name: header[i], prop: i})
    }
    this.previewRows = csv;
  }


  public resetUpload(){
    this.documentUploader.clearQueue();
    this.fileReady = false;
    this.isTxtFile = false;
    this.rawTextContent = '';
    this.previewColumns = [];
    this.previewRows = null;
  }

  changeFileType(){
    this.documentUploader.clearQueue();
    this.fileReady = false;
    this.isTxtFile = false;
    this.rawTextContent = '';
    this.previewColumns = [];
    this.previewRows = null;
  }

  parseFile(queueString: string){
    this.isParsing = true;
    this.documentUploaderService.patchProcessQueue(queueString)
      .toPromise()
      .then(resp => {
        ConsoleLogger.logInfo(resp);
        this.isParsing = false;
      },err => {
        console.error(err);
        this.isParsing = false;
      })
  }
}



// WEBPACK FOOTER //
// ./src/app/developer/file-import/file-import.component.ts